import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../api';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Notice } from '../components/Notice';
import { PageHeader } from '../components/PageHeader';

export interface BillingPhone {
  installationId: string;
  name: string;
  plan: 'free' | 'pro';
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface BillingOverview {
  billingEnabled: boolean;
  temporaryOffer: boolean;
  configured: boolean;
  mode: 'test' | 'live';
  currency: 'GBP';
  monthlyAmount: number;
  yearlyAmount: number;
  phones: BillingPhone[];
}

const stripeHosts = new Set(['checkout.stripe.com', 'billing.stripe.com']);

export function trustedStripeUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && stripeHosts.has(url.hostname) && !url.username && !url.password
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

export function formatPlanAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount / 100);
}

function planStatus(phone: BillingPhone): { label: string; detail: string } {
  if (['past_due', 'unpaid', 'incomplete', 'paused'].includes(phone.status)) return { label: 'Payment needs attention', detail: 'Manage your existing subscription with Stripe to restore paid access. The free allowance remains available.' };
  if (phone.plan === 'free') return { label: 'Free', detail: '100 automated API and plugin messages per day.' };
  const periodEnd = phone.currentPeriodEnd ? new Date(phone.currentPeriodEnd).toLocaleDateString() : null;
  if (phone.cancelAtPeriodEnd) return { label: 'Pro · canceling', detail: periodEnd ? `Paid access continues until ${periodEnd}.` : 'Cancellation is scheduled.' };
  if (phone.status === 'active' || phone.status === 'trialing') return { label: 'Pro · active', detail: periodEnd ? `Current period ends ${periodEnd}.` : 'Subscription active.' };
  return { label: 'Pro · expired', detail: 'Choose a plan to restore Pro for this phone.' };
}

export function BillingPage() {
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async (showResult = false) => {
    setError('');
    try {
      const result = await api<BillingOverview>('/api/billing');
      setOverview(result);
      setSelectedId((current) => result.phones.some((phone) => phone.installationId === current) ? current : result.phones[0]?.installationId ?? '');
      if (showResult) setSuccess('Billing status refreshed.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load billing details.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const returnedFromBilling = new URLSearchParams(window.location.search).has('billing');
    void load(returnedFromBilling);
  }, [load]);

  const selected = useMemo(() => overview?.phones.find((phone) => phone.installationId === selectedId) ?? null, [overview, selectedId]);
  const status = selected ? planStatus(selected) : null;
  const existingSubscription = Boolean(selected && !['free', 'canceled', 'incomplete_expired'].includes(selected.status));

  async function openCheckout(interval: 'month' | 'year') {
    if (!selected || !overview?.configured || !overview.billingEnabled) return;
    setBusy(interval); setError(''); setSuccess('');
    try {
      const result = await api<{ url: string }>('/api/billing/checkout', { method: 'POST', body: JSON.stringify({ installationId: selected.installationId, interval }) });
      const destination = trustedStripeUrl(result.url);
      if (!destination) throw new Error('untrusted_checkout_url');
      window.location.assign(destination);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not open secure Stripe checkout.');
      setBusy('');
    }
  }

  async function openPortal() {
    setBusy('portal'); setError(''); setSuccess('');
    try {
      const result = await api<{ url: string }>('/api/billing/portal', { method: 'POST' });
      const destination = trustedStripeUrl(result.url);
      if (!destination) throw new Error('untrusted_portal_url');
      window.location.assign(destination);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not open the secure Stripe billing portal.');
      setBusy('');
    }
  }

  return <div className="page narrow-page">
    <PageHeader eyebrow="API and plugin usage" title="Plans and billing" description="Choose a plan separately for each phone used by APIs or connected website plugins. Local messaging, your synced web inbox, and browser sending stay free." actions={<button className="button button-tonal" type="button" disabled={loading || Boolean(busy)} onClick={() => void load(true)}><Icon name="refresh" /> Refresh</button>} />
    {overview?.temporaryOffer && <section className="offer-panel"><span className="offer-badge">Temporary free offer</span><h2>Everything is free for now.</h2><p>All features are included, with no commercial daily allowance for API and plugin sends. No subscription or payment is needed.</p><p>Carrier charges and sending safeguards still apply. Paid plans will apply only after pricing is enabled; earlier free-offer messages won&apos;t count.</p></section>}
    {overview?.billingEnabled && overview.mode === 'test' && <Notice kind="warning"><strong>Test mode:</strong>&nbsp;Stripe will not create a live subscription on this site.</Notice>}
    {overview?.billingEnabled && !overview.configured && <Notice kind="warning">Checkout is temporarily unavailable. Your current plan remains in effect.</Notice>}
    {error && <Notice kind="error">{error}</Notice>}
    {success && <Notice kind="success">{success}</Notice>}
    {loading ? <div className="skeleton-list" aria-label="Loading billing details"><span /><span /></div>
      : !overview || overview.phones.length === 0 ? <EmptyState icon="phone_android" title="No phones linked to this account" body="Sign in to this Messavo account on an Android phone first. SMS sync does not need to be enabled to link the phone for API or plugin billing." />
        : <div className="billing-layout">
          <section className="billing-summary">
            <div className="billing-controls">
              <label>Phone<select value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>{overview.phones.map((phone) => <option value={phone.installationId} key={phone.installationId}>{phone.name}</option>)}</select></label>
              {selected && selected.status !== 'free' && <button className="button button-tonal" type="button" disabled={!overview.configured || Boolean(busy)} onClick={() => void openPortal()}><Icon name="open_in_new" /> {busy === 'portal' ? 'Opening…' : 'Manage with Stripe'}</button>}
            </div>
            {status && <><div className="billing-status-row"><h2>{selected?.name}</h2><span className={`status-pill ${selected?.plan === 'pro' ? 'status-ready' : ''}`}>{overview.temporaryOffer ? 'Free offer' : status.label}</span></div><p>{overview.temporaryOffer ? 'All features available. No API/plugin commercial allowance during this offer.' : status.detail}</p></>}
          </section>
          {overview.billingEnabled && <div className="billing-plan-grid">
            <article className="billing-plan"><span className="eyebrow">Monthly</span><div className="billing-plan-price">{formatPlanAmount(overview.monthlyAmount, overview.currency)} <small>per phone / month</small></div><p>Removes the 100-message commercial daily cap for this phone while active.</p><button className="button button-primary" type="button" disabled={!overview.configured || existingSubscription || Boolean(busy)} onClick={() => void openCheckout('month')}>{busy === 'month' ? 'Opening Stripe…' : 'Choose monthly'}</button></article>
            <article className="billing-plan"><span className="eyebrow">Annual</span><div className="billing-plan-price">{formatPlanAmount(overview.yearlyAmount, overview.currency)} <small>per phone / year</small></div><p>One annual subscription for the selected phone, managed through Stripe.</p><button className="button button-primary" type="button" disabled={!overview.configured || existingSubscription || Boolean(busy)} onClick={() => void openCheckout('year')}>{busy === 'year' ? 'Opening Stripe…' : 'Choose annual'}</button></article>
          </div>}
          {overview.billingEnabled && <Notice>Free includes 100 API and plugin messages per phone per day, shared across that phone&apos;s connections and reset at 00:00 UTC. Pro removes that commercial cap. Carrier, consent, safety, and abuse-prevention limits still apply; there are no automatic overage charges.</Notice>}
          <p className="muted-copy">Stripe stores and processes payment details. Messavo does not send Stripe your SMS bodies, recipients, or phone numbers.</p>
        </div>}
  </div>;
}
